<template>
  <ListView :title="$t('pages.vatSetting.list.title')">
    <template #navigationButtons>
      <NavigationLinkButton
        :to="{ name: 'vatSettingEdit', params: { mandantId: mandant.id, vatSettingId: '0' } }"
        :icon="Icon.ADD"
        :title="$t('message.new')"
      />
    </template>
    <GenericTable
      ref="tableComponent"
      :endpoint-url="vatSettingGenericTable.getPath(mandant.id)"
      :schema="vatSettingGenericTable.schema"
      :column-definitions="columnDefinitions"
      :row-config="{
        type: GenericTableRowConfigType.LINK,
        disabled: false,
        to: (vatSetting) => ({ name: 'vatSettingEdit', params: { vatSettingId: vatSetting.id } }),
      }"
    >
      <template #state="{ entity }">
        <StateTag :state="entity.state" />
      </template>
      <template #vatRate="{ entity }"> {{ entity.vatRate }}% </template>
      <template #type="{ entity }">
        <BaseTag v-if="entity.type" class="!bg-blue">{{ $t(`vatSettingType.${entity.type}`) }}</BaseTag>
      </template>
      <template #_actions="{ entity }">
        <IconButtonColumn>
          <IconButton
            :icon="Icon.DELETE"
            :tooltip-text="$t('message.deleteRecord')"
            @click.prevent="() => deleteRecord(entity)"
          />
        </IconButtonColumn>
      </template>
    </GenericTable>
  </ListView>
</template>

<script setup lang="ts">
import { Ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";
import { Icon } from "../../assets/icons/Icon";
import GenericTable from "../../common/genericTable/components/GenericTable.vue";
import { GenericTableColumnDefinitions } from "../../common/genericTable/type.genericTableColumnDefinition";
import { GenericTableRowConfigType } from "../../common/genericTable/type.genericTableRowConfig";
import BaseTag from "../../components/BaseTag.vue";
import IconButton from "../../components/IconButton.vue";
import IconButtonColumn from "../../components/IconButtonColumn.vue";
import ListView from "../../components/ListView.vue";
import NavigationLinkButton from "../../components/NavigationLinkButton.vue";
import StateTag from "../../components/StateTag.vue";
import { MandantDto } from "../../mandant/dto.mandant";
import { useMandant } from "../../mandant/service.mandant";
import { getUserConfirmationAsBoolean, setToastMessage, setToastMessageLoading } from "../../util/SweetalertHelper";
import { VatSettingGenericTable, vatSettingGenericTable } from "../genericTable.vatSetting";
import { deleteVatSetting } from "../service.vatSetting";

const { t } = useI18n();

const mandantStore = useMandant();
const { currentMandant } = storeToRefs(mandantStore);
const mandant = currentMandant as Ref<MandantDto>;

const tableComponentRef = useTemplateRef("tableComponent");

const columnDefinitions = {
  id: {
    columnName: t("message.id"),
    isSearchable: false,
    isVisible: true,
    shouldFillColumn: false,
    isFilterable: true,
    isSortable: true,
  },
  state: {
    columnName: t("message.state"),
    isSearchable: false,
    isVisible: true,
    shouldFillColumn: false,
    isFilterable: true,
    isSortable: true,
  },
  description: {
    columnName: t("message.vatSetting.description"),
    isSearchable: true,
    isVisible: true,
    shouldFillColumn: true,
    isFilterable: true,
    isSortable: true,
  },
  vatRate: {
    columnName: t("message.vatSetting.rate"),
    isSearchable: false,
    isVisible: true,
    shouldFillColumn: false,
    isFilterable: true,
    isSortable: true,
  },
  type: {
    columnName: t("message.vatSetting.type"),
    isSearchable: false,
    isVisible: true,
    shouldFillColumn: false,
    isFilterable: true,
    isSortable: true,
  },
  _actions: {
    isCustomColumn: true,
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    isSearchable: false,
    shouldFillColumn: false,
    columnName: t("message.actions"),
  },
} satisfies GenericTableColumnDefinitions<VatSettingGenericTable["schema"]>;

async function deleteRecord(item: { id: number }) {
  const isConfirmed = await getUserConfirmationAsBoolean();

  if (isConfirmed) {
    setToastMessageLoading();

    if (item.id) {
      await deleteVatSetting({ vatSettingId: item.id, mandantId: mandant.value.id });

      setToastMessage({ icon: "success", title: t("message.recordDeleted") });

      await tableComponentRef.value?.refresh();
    }
  }
}
</script>
